/**
 * AuthContext - source de verite du user authentifie.
 * - Au mount : GET /api/auth/me (silencieux si 401)
 * - Apres login/verify : refresh
 * - Apres logout : clear
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import {
  authApi, getTrustedDeviceId, setTrustedDeviceId,
  type AuthUser, type LoginResult, type LoginCompleted,
} from './authApi';

interface AuthState {
  user:            AuthUser | null;
  isAuthenticated: boolean;
  isLoading:       boolean;
  error:           string | null;
}

interface AuthContextValue extends AuthState {
  refresh:            () => Promise<void>;
  requestMagicLink:   (email: string) => Promise<void>;
  verifyMagicLink:    (token: string) => Promise<AuthUser>;
  /** Etape 1 : password. Retourne otp_required ou completed (session posee). */
  login:              (email: string, password: string) => Promise<LoginResult>;
  /** Etape 2 : verifie l OTP, pose la session, memorise l appareil si demande. */
  verifyLoginOtp:     (challengeId: string, code: string, rememberDevice: boolean) => Promise<LoginCompleted>;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword:      (token: string, newPassword: string) => Promise<void>;
  setPassword:        (newPassword: string, currentPassword?: string) => Promise<void>;
  logout:             () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser]       = useState<AuthUser | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const me = await authApi.me();
      setUser(me);
    } catch {
      // 401 ou autre = pas authentifie, c est normal
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial : check la session au mount
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Ecoute l event global 'nis365:session-expired' dispatch par apiClient
  // quand un fetch renvoie 401. On clear le user → AuthGate detecte et redirige
  // vers /login avec preservation du chemin courant via state.from.
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      setError('Session expirée. Veuillez vous reconnecter.');
    };
    window.addEventListener('nis365:session-expired', onExpired);
    return () => window.removeEventListener('nis365:session-expired', onExpired);
  }, []);

  const requestMagicLink = useCallback(async (email: string) => {
    setError(null);
    await authApi.requestMagicLink(email);
  }, []);

  const verifyMagicLink = useCallback(async (token: string) => {
    setError(null);
    const me = await authApi.verifyMagicLink(token);
    setUser(me);
    return me;
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    const res = await authApi.login(email, password, getTrustedDeviceId());
    if (res.step === 'completed') {
      setUser(res);
      if (res.deviceId) setTrustedDeviceId(res.deviceId);
    }
    return res;
  }, []);

  const verifyLoginOtp = useCallback(async (challengeId: string, code: string, rememberDevice: boolean) => {
    setError(null);
    const me = await authApi.verifyLoginOtp(challengeId, code, rememberDevice);
    setUser(me);
    if (me.deviceId) setTrustedDeviceId(me.deviceId);
    return me;
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    setError(null);
    await authApi.requestPasswordReset(email);
  }, []);

  const resetPassword = useCallback(async (token: string, newPassword: string) => {
    setError(null);
    await authApi.resetPassword(token, newPassword);
  }, []);

  const setPassword = useCallback(async (newPassword: string, currentPassword?: string) => {
    await authApi.setPassword(newPassword, currentPassword);
    // Met a jour user.hasPassword pour refleter le changement dans la UI
    setUser((u) => (u ? { ...u, hasPassword: true } : u));
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    refresh,
    requestMagicLink,
    verifyMagicLink,
    login,
    verifyLoginOtp,
    requestPasswordReset,
    resetPassword,
    setPassword,
    logout,
  }), [user, isLoading, error, refresh, requestMagicLink, verifyMagicLink, login, verifyLoginOtp, requestPasswordReset, resetPassword, setPassword, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
